import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const TestimonialCard = ({ testimonial, isActive }) => (
  <div 
    className={`
      relative transition-all duration-1000 ease-out
      ${isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full hidden'}
    `}
  >
    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#64D652]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#A7E787]/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      
      {/* Quote Mark */}
      <div className="absolute -top-2 left-8 text-8xl text-[#64D652] opacity-10 font-serif leading-none">"</div>
      
      {/* Content */}
      <div className="relative">
        <p className="text-xl md:text-2xl text-neutral-700 leading-relaxed mb-8 font-light">
          {testimonial.quote}
        </p>
        
        <div className="flex items-center gap-6">
          {/* Author Image */}
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden ring-4 ring-[#64D652]/10">
              <img 
                src={testimonial.image} 
                alt={testimonial.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#64D652] rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-3.5 h-3.5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          {/* Author Info */}
          <div>
            <h4 className="text-lg font-medium text-neutral-800">{testimonial.name}</h4>
            <p className="text-neutral-500">{testimonial.role}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: true,
      mirror: false,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
    });
  }, []);

  const testimonials = [
    {
      quote: "The personalized wellness program has completely transformed my approach to health. The combination of ancient wisdom and modern science is truly revolutionary.",
      name: "Sarah Anderson",
      role: "Executive & Wellness Enthusiast",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80"
    },
    {
      quote: "What sets Vedastik apart is their attention to detail and truly personalized approach. The AI-driven insights combined with expert guidance have helped me achieve remarkable results.",
      name: "Michael Chen",
      role: "Tech Entrepreneur",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80"
    },
    {
      quote: "The at-home wellness sessions are a game-changer. The convenience combined with the expertise of their practitioners has made maintaining my wellness routine effortless.",
      name: "Emma Thompson",
      role: "Wellness Coach",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80"
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="relative py-24 bg-gradient-to-b from-neutral-50 to-white">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-[800px] h-[800px] -right-40 -top-40 rounded-full bg-gradient-to-br from-[#64D652]/5 to-[#A7E787]/5 blur-3xl" />
        <div className="absolute w-[600px] h-[600px] -left-20 -bottom-20 rounded-full bg-gradient-to-tr from-[#64D652]/5 to-[#A7E787]/5 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-20" data-aos="fade-up">
          <div className="inline-block px-4 py-2 rounded-full bg-[#64D652]/10 text-[#4CA440] text-sm font-medium mb-6">
            Client Stories
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-neutral-800 tracking-tight">
            Transformative Experiences
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Discover how our personalized wellness programs have helped clients
            achieve their optimal health and wellbeing.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="max-w-4xl mx-auto" data-aos="fade-up" data-aos-delay="200">
          <div className="mb-12">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                testimonial={testimonial}
                isActive={index === currentIndex}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-8 pb-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full border-2 border-neutral-200 flex items-center justify-center text-neutral-400 hover:border-[#64D652] hover:text-[#64D652] transition-colors duration-300"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Indicators */}
            <div className="flex gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`
                    w-2.5 h-2.5 rounded-full transition-all duration-300
                    ${index === currentIndex ? 'w-8 bg-[#64D652]' : 'bg-neutral-200 hover:bg-neutral-300'}
                  `}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full border-2 border-neutral-200 flex items-center justify-center text-neutral-400 hover:border-[#64D652] hover:text-[#64D652] transition-colors duration-300"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection; 